import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../Helpers/Handler";
import { disconnectSocket } from "../Helpers/socketClient";
import { unregisterThisDevice } from "../Helpers/pushDevice";
import { deleteAllCookies, getCookie, reloadWindowToPath, setCookie, storageRemoveItem } from "../Helpers/Utils";

const AuthContext = createContext();

function AuthenticateProvider({ children }) {
    const token = getCookie("token");
    const [isAuthenticated, setIsAuthenticated] = useState(!!token);
    const [siteDetails, setSiteDetails] = useState({});
    const [profile, setProfile] = useState({});

    const logIn = (authToken, userId) => {
        setCookie("token", authToken);
        setCookie("userId", userId);
        storageRemoveItem("filter");
        setIsAuthenticated(true);
    };

    const logOut = async () => {
        // Stop this browser receiving the admin's notifications before the session ends.
        await unregisterThisDevice();
        await Handler({ method: "post", url: "/admin-auth/logout-all", data: {} });
        toast.success("Logout successfully");
        // Explicit, synchronous cleanup ahead of the hard reload below -
        // don't rely on SocketPresenceProvider's isAuthenticated effect for
        // this, since setIsAuthenticated(false) and reloadWindowToPath() can
        // both run before React gets a chance to flush and re-run effects.
        disconnectSocket();
        deleteAllCookies();
        storageRemoveItem("filter");
        setIsAuthenticated(false);
        reloadWindowToPath("/");
    };

    const getSiteDetails = useCallback(async () => {
        const response = await Handler({ method: "GET", url: "/admin/business/view/site-details" });
        setSiteDetails(response?.success ? response?.data?.data || {} : {});
    }, []);

    const getProfile = useCallback(async () => {
        const response = await Handler({ method: "GET", url: "/admin-auth/me" });
        setProfile(response?.success ? response?.data?.data || {} : {});
    }, []);

    useEffect(() => {
        if (token) setIsAuthenticated(true);
    }, [token]);

    useEffect(() => {
        if (!isAuthenticated) return;
        getSiteDetails();
        getProfile();
    }, [isAuthenticated, getSiteDetails, getProfile]);

    return (
        <AuthContext.Provider value={{ logIn, logOut, isAuthenticated, siteDetails, profile, getSiteDetails, getProfile }}>
            {children}
        </AuthContext.Provider>
    );
}

export default AuthenticateProvider;

export const useAuth = () => useContext(AuthContext);
